import {useState, useCallback, createContext, useContext} from "react";

export interface DocumentPreviewTarget {
   id: string;
   fileName?: string;
   pages?: string[];
}

interface UseDocumentPreviewReturn {
   /** Open the preview sheet for a document */
   openDocument: (doc: DocumentPreviewTarget) => void;
   /** Props to spread on DocumentPreviewSheet */
   previewProps: {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      document: DocumentPreviewTarget | null;
   };
}

export function useDocumentPreview(): UseDocumentPreviewReturn {
   const [open, setOpen] = useState(false);
   const [document, setDocument] = useState<DocumentPreviewTarget | null>(null);

   const openDocument = useCallback((doc: DocumentPreviewTarget) => {
      setDocument(doc);
      setOpen(true);
   }, []);

   return {
      openDocument,
      previewProps: {
         open,
         onOpenChange: setOpen,
         document,
      },
   };
}

/** Context so deeply-nested components can open the preview without rendering their own sheet. */
const DocumentPreviewContext = createContext<((doc: DocumentPreviewTarget) => void) | null>(null);

export const DocumentPreviewProvider = DocumentPreviewContext.Provider;

export function useDocumentPreviewContext(): (doc: DocumentPreviewTarget) => void {
   const ctx = useContext(DocumentPreviewContext);
   if (!ctx) throw new Error("useDocumentPreviewContext must be used within a DocumentPreviewProvider");
   return ctx;
}
